import React, { useMemo, useState } from "react";
import { Button, Empty, Input } from "antd";
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import GraphicWarehouse from "components/graphic-warehouse";
import { Localize } from "components/service";
import { useTranslation } from "components/use-hooks";

const {
    isShelfLabelOccupied,
    normalizeShelfNumber
} = GraphicWarehouse.Helpers.ShelfAvailability;

const StandShelfPickerModal = ({
    action,
    onSelect,
    currentValue = "",
    saving = false,
    serverError = null
}) => {
    const translations = useTranslation();
    const [ searchText, setSearchText ] = useState("");
    const [ customShelf, setCustomShelf ] = useState("");
    const [ customError, setCustomError ] = useState("");

    const {
        loading,
        standardLabels,
        freeStandardLabels,
        occupiedLabels
    } = GraphicWarehouse.Hooks.useShelfAvailability({
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first'
    });

    const translate = (key) => translations[ key ] || key;
    const currentShelf = normalizeShelfNumber(currentValue);
    const hasCurrentShelf = Boolean(currentShelf);
    const normalizedSearch = normalizeShelfNumber(searchText).toLowerCase();
    const selectableShelfLabels = useMemo(() => {
        if (!hasCurrentShelf || !standardLabels.includes(currentShelf))
        {
            return freeStandardLabels;
        }

        const freeShelfLabels = new Set(freeStandardLabels);

        return standardLabels.filter(label => freeShelfLabels.has(label) || label === currentShelf);
    }, [ currentShelf, freeStandardLabels, hasCurrentShelf, standardLabels ]);

    const filteredShelfLabels = useMemo(() => {
        if (!normalizedSearch)
        {
            return selectableShelfLabels;
        }

        return selectableShelfLabels.filter(label => label.toLowerCase().includes(normalizedSearch));
    }, [ selectableShelfLabels, normalizedSearch ]);

    const handleShelfSelect = (shelfNumber) => {
        if (saving || loading)
        {
            return;
        }

        setCustomError("");
        onSelect(shelfNumber, action);
    };

    const handleResetShelf = () => {
        if (saving || !hasCurrentShelf)
        {
            return;
        }

        setCustomError("");
        onSelect("", action);
    };

    const handleCustomSubmit = () => {
        if (saving || loading)
        {
            return;
        }

        const normalizedCustomShelf = normalizeShelfNumber(customShelf);

        if (!normalizedCustomShelf)
        {
            setCustomError("WAREHOUSE.Text_CustomShelfRequired");
            return;
        }

        if (isShelfLabelOccupied(occupiedLabels, normalizedCustomShelf))
        {
            setCustomError("WAREHOUSE.Text_CustomShelfOccupied");
            return;
        }

        setCustomError("");
        onSelect(normalizedCustomShelf, action);
    };

    return (
        <div className="stand-shelf-picker">
            <strong className="modal-title"><Localize>FORMS.Title_ChooseShelf</Localize></strong>

            { hasCurrentShelf &&
                <div className="stand-shelf-picker-current">
                    <span><Localize>WAREHOUSE.Label_CurrentShelf</Localize>:</span>
                    <strong>{ currentShelf }</strong>
                </div>
            }

            <div className="stand-shelf-picker-toolbar">
                <Input
                    allowClear
                    prefix={ <SearchOutlined /> }
                    value={ searchText }
                    onChange={ event => setSearchText(event.target.value) }
                    placeholder={ translate("SEARCH.Input_Placeholder_FreeShelves") }
                />
                <div className="stand-shelf-picker-count">
                    <Localize>WAREHOUSE.Label_FreeShelves</Localize>: <strong>{ loading ? <LoadingOutlined /> : freeStandardLabels.length }</strong>
                </div>
            </div>

            <div className="stand-shelf-picker-board">
                { loading ? (
                    <div className="stand-shelf-picker-loading">
                        <LoadingOutlined />
                    </div>
                ) : filteredShelfLabels.length ? (
                    <div className="stand-shelf-picker-grid">
                        { filteredShelfLabels.map(label => {
                            const isCurrentShelf = label === currentShelf;

                            return (
                            <button
                                type="button"
                                key={ label }
                                className={ `stand-shelf-picker-cell${ isCurrentShelf ? " current" : "" }` }
                                disabled={ saving || loading }
                                aria-pressed={ isCurrentShelf }
                                onClick={ () => handleShelfSelect(label) }
                            >
                                { label }
                            </button>
                            );
                        }) }
                    </div>
                ) : (
                    <div className="stand-shelf-picker-empty">
                        <Empty
                            image={ Empty.PRESENTED_IMAGE_SIMPLE }
                            description={ <Localize>WAREHOUSE.Text_NoFreeShelves</Localize> }
                        />
                    </div>
                ) }
            </div>

            <div className="stand-shelf-picker-custom">
                <span className="stand-shelf-picker-section-title">
                    <Localize>WAREHOUSE.Label_CustomShelf</Localize>
                </span>
                <div className="stand-shelf-picker-custom-row">
                    <Input
                        value={ customShelf }
                        onChange={ event => {
                            setCustomShelf(event.target.value);
                            if (customError)
                            {
                                setCustomError("");
                            }
                        } }
                        onPressEnter={ handleCustomSubmit }
                        placeholder={ translate("FORMS.Input_Placeholder_CustomShelf") }
                        disabled={ saving || loading }
                    />
                    <Button
                        type="primary"
                        loading={ saving }
                        disabled={ loading }
                        onClick={ handleCustomSubmit }
                    >
                        <Localize>GLOBAL.Button_Text_Save</Localize>
                    </Button>
                </div>
                { customError &&
                    <span className="stand-shelf-picker-error">
                        <Localize>{ customError }</Localize>
                    </span>
                }
                { serverError &&
                    <span className="stand-shelf-picker-error">
                        { serverError }
                    </span>
                }
            </div>

            <div className="stand-shelf-picker-actions">
                <Button onClick={ action }>
                    <Localize>GLOBAL.Button_Text_Cancel</Localize>
                </Button>
                <Button
                    className="ant-btn-secondary"
                    disabled={ saving || !hasCurrentShelf }
                    onClick={ handleResetShelf }
                >
                    { hasCurrentShelf ?
                        <Localize shelfNumber={ currentShelf }>WAREHOUSE.Button_Text_ResetShelfValue</Localize> :
                        <Localize>WAREHOUSE.Button_Text_ResetShelf</Localize>
                    }
                </Button>
            </div>
        </div>
    );
};

export default StandShelfPickerModal;
